import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import AmbientBackground from "../components/AmbientBackground";
import AboutWorkVisual from "../components/AboutWorkVisual";
import {
  HERO_COPY_TYPE_DELAY_MS,
  HeroTypingLead,
  HeroTypingTitle,
} from "../components/HeroTypingIntro";
import MarketingMediaSlot from "../components/MarketingMediaSlot";
import IconInterfaces from "../components/icons/IconInterfaces";
import IconAutomation from "../components/icons/IconAutomation";
import IconAnalytics from "../components/icons/IconAnalytics";
import IconPipelines from "../components/icons/IconPipelines";
import impactServiceIcon from "../assets/about-impact-icons/service.svg";
import impactTimeIcon from "../assets/about-impact-icons/doorlooptijd.svg";
import impactRevenueIcon from "../assets/about-impact-icons/omzet.svg";
import impactControlIcon from "../assets/about-impact-icons/grip-op-uitvoering.svg";
import { trackEvent } from "../lib/analytics";
import {
  AnimatedSection,
  ContentPageInner,
  ContentPageShell,
  GlassPanel,
  SectionHeading,
  SectionIntro,
  SectionKicker,
  SectionLead,
  SectionLink,
} from "../styles/contentPage";

const heroImpactIcons = [
  impactServiceIcon,
  impactTimeIcon,
  impactRevenueIcon,
  impactControlIcon,
];

function renderHeroImpactVisual(index) {
  const icon = heroImpactIcons[index] || heroImpactIcons[0];

  return (
    <HeroImpactImage
      src={icon}
      alt=""
      aria-hidden="true"
      data-fit-impact-visual
      decoding="async"
    />
  );
}

export default function AboutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const heroLead = t("aboutPage.hero.lead", { returnObjects: true });
  const heroSupport = t("aboutPage.hero.support", { returnObjects: true }) || {
    fitItems: [],
  };
  const workItems = t("aboutPage.work.items", { returnObjects: true });
  const whyItems = t("whyMakdis.items", { returnObjects: true });
  const projects = t("aboutPage.projects.items", { returnObjects: true });
  const steps = t("aboutPage.process.steps", { returnObjects: true });
  const personalLead = t("aboutPage.personal.lead", { returnObjects: true });
  const personalImage = t("aboutPage.personal.image", { returnObjects: true });
  const strategyLead = t("aboutPage.strategyBridge.lead", { returnObjects: true });
  const mediaSlotLabel = t("aboutPage.media.slot");

  const goToContact = () => navigate("/contact");
  const goToServices = () => navigate("/services");
  const goToWork = () => navigate("/work");
  const whyIcons = [IconInterfaces, IconAutomation, IconAnalytics, IconPipelines];

  const handleProjectClick = (project) => {
    trackEvent("case_card_click", {
      source: "about-page",
      label: project.title,
      external: Boolean(project.externalUrl),
    });

    if (project.externalUrl) {
      window.open(project.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(project.href || "/work");
  };

  const handleWorkItemClick = (item) => {
    if (!item.href) return;

    trackEvent("cta_strategy_call_click", {
      source: "about-work-card",
      destination: item.href,
      label: item.title,
    });

    navigate(item.href);
  };

  return (
    <Wrap id="about" className="page-container">
      <AmbientBackground />
      <Inner>
        <AnimatedSection $i={0}>
          <Hero>
            <TypingHeroTitle data-hero-title-typing>
              {t("aboutPage.hero.title")}
            </TypingHeroTitle>
            <HeroCopy
              data-hero-copy-typing
              text={heroLead}
              separator={"\n\n"}
              initialDelayMs={HERO_COPY_TYPE_DELAY_MS}
            />
            <HeroSupportStrip>
              <HeroSupportBlock>
                <HeroSupportLabel>{heroSupport.notForLabel}</HeroSupportLabel>
                <HeroSupportText>{heroSupport.notForText}</HeroSupportText>
              </HeroSupportBlock>
              <HeroSupportBlock data-fit-support="true" $alignRight>
                <HeroSupportLabel>{heroSupport.fitLabel}</HeroSupportLabel>
                <HeroSupportList>
                  {heroSupport.fitItems.map((item, idx) => (
                    <HeroSupportChip key={item}>
                      {renderHeroImpactVisual(idx)}
                      <span>{item}</span>
                    </HeroSupportChip>
                  ))}
                </HeroSupportList>
              </HeroSupportBlock>
            </HeroSupportStrip>
          </Hero>
        </AnimatedSection>

        <AnimatedSection $i={1}>
          <SectionIntro>
            <SectionKicker>{t("aboutPage.work.title")}</SectionKicker>
            <SectionHeading>{t("aboutPage.work.heading")}</SectionHeading>
            <SectionLead>{t("aboutPage.work.lead")}</SectionLead>
          </SectionIntro>

          <CapabilityGrid>
            {workItems.map((item) => (
              <CapabilityCard
                key={item.title}
                type="button"
                onClick={() => handleWorkItemClick(item)}
              >
                {item.visual ? (
                  <CapabilityVisual
                    variant={item.visual}
                    title={item.alt || item.title}
                    aspect="4 / 3"
                  />
                ) : (
                  <CapabilityMedia
                    src={item.image}
                    alt={item.alt || item.title}
                    label={mediaSlotLabel}
                    hint={item.placeholder}
                    aspect="4 / 3"
                  />
                )}
                <CapabilityLabel>{item.label}</CapabilityLabel>
                <CapabilityTitle>{item.title}</CapabilityTitle>
                <CapabilityDesc>{item.description}</CapabilityDesc>
              </CapabilityCard>
            ))}
          </CapabilityGrid>

          <ActionRow>
            <SectionLink onClick={goToServices}>{t("aboutPage.work.cta")} →</SectionLink>
          </ActionRow>
        </AnimatedSection>

        <AnimatedSection $i={2}>
          <SectionIntro>
            <SectionKicker>{t("whyMakdis.title")}</SectionKicker>
            <SectionHeading>{t("whyMakdis.heading")}</SectionHeading>
          </SectionIntro>

          <WhyGrid data-why-layout="grid">
            {whyItems.map((item, idx) => (
              <WhyCard key={item.title} data-why-card $i={idx}>
                <WhyCardSignal aria-hidden="true" data-why-card-signal>
                  <span />
                  <span />
                  <span />
                </WhyCardSignal>
                <WhyCardHeader>
                  <WhyIconWrap aria-hidden="true" data-why-icon>
                    {React.createElement(whyIcons[idx % whyIcons.length], {
                      width: 42,
                      height: 42,
                      "aria-hidden": true,
                    })}
                  </WhyIconWrap>
                  <WhyNumber>{String(idx + 1).padStart(2, "0")}</WhyNumber>
                </WhyCardHeader>
                <WhyCardBody>
                  <WhyTitle>{item.title}</WhyTitle>
                  <WhyDesc>{item.description}</WhyDesc>
                </WhyCardBody>
              </WhyCard>
            ))}
          </WhyGrid>
        </AnimatedSection>

        <AnimatedSection $i={3}>
          <SectionIntro>
            <SectionKicker>{t("aboutPage.projects.title")}</SectionKicker>
            <SectionHeading>{t("aboutPage.projects.heading")}</SectionHeading>
            <SectionLead>{t("aboutPage.projects.lead")}</SectionLead>
          </SectionIntro>

          <ProjectsStack>
            {projects.map((project, idx) => (
              <ProjectRow
                key={project.title}
                type="button"
                $reverse={idx % 2 === 1}
                onClick={() => handleProjectClick(project)}
              >
                <ProjectMedia
                  src={project.image}
                  alt={project.alt}
                  label={mediaSlotLabel}
                  hint={project.placeholder}
                  aspect="16 / 10"
                  objectPosition={project.position || "center"}
                  objectFit={project.objectFit || "cover"}
                  objectScale={project.objectScale || 1}
                />
                <ProjectCopy>
                  <ProjectMeta>
                    <ProjectLabel>{project.label}</ProjectLabel>
                    {project.externalUrl ? (
                      <ProjectTag>{t("aboutPage.projects.externalLabel")}</ProjectTag>
                    ) : null}
                  </ProjectMeta>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDesc>{project.description}</ProjectDesc>
                </ProjectCopy>
              </ProjectRow>
            ))}
          </ProjectsStack>

          <ActionRow>
            <SectionLink onClick={goToWork}>{t("aboutPage.projects.cta")} →</SectionLink>
          </ActionRow>
        </AnimatedSection>

        <AnimatedSection $i={4}>
          <SectionIntro>
            <SectionKicker>{t("aboutPage.process.title")}</SectionKicker>
            <SectionHeading>{t("aboutPage.process.heading")}</SectionHeading>
            <SectionLead>{t("aboutPage.process.lead")}</SectionLead>
          </SectionIntro>

          <ProcessGrid>
            {steps.map((step, idx) => (
              <StepCard key={step.title}>
                <StepNumber>{String(idx + 1).padStart(2, "0")}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.description}</StepDesc>
              </StepCard>
            ))}
          </ProcessGrid>
        </AnimatedSection>

        <AnimatedSection $i={5}>
          <FounderGrid>
            <FounderMedia
              src={personalImage.src}
              alt={personalImage.alt}
              label={mediaSlotLabel}
              hint={personalImage.placeholder}
              aspect="4 / 5"
            />
            <FounderColumn>
              <FounderCopy>
                <FounderKicker>{t("aboutPage.personal.role")}</FounderKicker>
                <FounderName>{t("aboutPage.personal.name")}</FounderName>
                {personalLead.map((paragraph, idx) => (
                  <FounderText key={idx}>{paragraph}</FounderText>
                ))}
              </FounderCopy>
            </FounderColumn>
          </FounderGrid>
        </AnimatedSection>

        <AnimatedSection $i={6}>
          <StrategyBridgePanel>
            <StrategyBridgeCopy>
              <SectionKicker>{t("aboutPage.strategyBridge.label")}</SectionKicker>
              <StrategyBridgeHeading>{t("aboutPage.strategyBridge.title")}</StrategyBridgeHeading>
              {strategyLead.map((paragraph, idx) => (
                <StrategyBridgeText key={idx}>{paragraph}</StrategyBridgeText>
              ))}
            </StrategyBridgeCopy>
            <StrategyBridgeActions>
              <SectionLink
                onClick={() => {
                  trackEvent("cta_strategy_call_click", {
                    source: "about-strategy-bridge",
                    destination: "services",
                  });
                  goToServices();
                }}
              >
                {t("aboutPage.strategyBridge.cta")} →
              </SectionLink>
            </StrategyBridgeActions>
          </StrategyBridgePanel>
        </AnimatedSection>

        <AnimatedSection $i={7}>
          <CtaPanel>
            <CtaCopy>
              <SectionKicker>{t("aboutPage.badge")}</SectionKicker>
              <CtaHeading>{t("aboutPage.cta.heading")}</CtaHeading>
              <CtaText>{t("aboutPage.cta.text")}</CtaText>
            </CtaCopy>
            <CtaActions>
              <CtaButton onClick={goToContact}>
                <span>{t("aboutPage.cta.button")}</span>
                <CtaArrow aria-hidden>→</CtaArrow>
              </CtaButton>
            </CtaActions>
          </CtaPanel>
        </AnimatedSection>
      </Inner>
    </Wrap>
  );
}

const Wrap = styled(ContentPageShell)`
  background: transparent;
`;

const Inner = styled(ContentPageInner)``;

const Hero = styled.div`
  width: 100%;
  max-width: 100%;
  position: relative;
  isolation: isolate;
  display: grid;
  gap: clamp(18px, 2vw, 24px);

  > h1 {
    position: relative;
    z-index: 1;
    max-width: min(72rem, 100%);
  }
`;

const TypingHeroTitle = styled(HeroTypingTitle)`
  z-index: 1;
  max-width: min(72rem, 100%);
`;

const HeroCopy = styled(HeroTypingLead)`
  z-index: 1;
  max-width: 58rem;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
`;

const HeroSupportStrip = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  gap: clamp(20px, 3vw, 32px);
  padding: clamp(22px, 3vw, 28px) 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const HeroSupportBlock = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
  ${(p) =>
    p.$alignRight &&
    `
    justify-self: end;
    justify-items: end;
    text-align: right;
  `}

  @media (max-width: 860px) {
    ${(p) =>
      p.$alignRight &&
      `
      justify-self: start;
      justify-items: start;
      text-align: left;
    `}
  }
`;

const HeroSupportLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.52);
`;

const HeroSupportText = styled.p`
  margin: 0;
  max-width: 32rem;
  font-size: 18px;
  line-height: 1.66;
  color: rgba(255, 255, 255, 0.82);
`;

const HeroSupportList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;

  @media (max-width: 860px) {
    justify-content: flex-start;
  }
`;

const HeroSupportChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 36px;
  padding: 0 14px 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025)),
    rgba(255, 255, 255, 0.035);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
`;

const HeroImpactImage = styled.img`
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  display: block;
  object-fit: contain;
  filter:
    brightness(0)
    saturate(100%)
    invert(94%)
    sepia(15%)
    saturate(762%)
    hue-rotate(158deg)
    brightness(101%)
    contrast(94%)
    drop-shadow(0 0 10px rgba(130, 216, 244, 0.18));
  opacity: 0.92;
`;

const CapabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const CapabilityCard = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  display: grid;
  align-content: start;
  gap: 16px;
  cursor: pointer;
  transition: transform 0.22s ease, opacity 0.22s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const CapabilityMedia = styled(MarketingMediaSlot)`
  border-radius: 28px;
`;

const CapabilityVisual = styled(AboutWorkVisual)`
  border-radius: 28px;
`;

const CapabilityLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const CapabilityTitle = styled.h3`
  margin: 0;
  font-size: clamp(26px, 2.8vw, 34px);
  line-height: 1.08;
`;

const CapabilityDesc = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.78);
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 28px;
`;

const whyCardSignal = keyframes`
  0%, 12% { transform: translate3d(-18px, 0, 0) scaleX(0.35); opacity: 0; }
  30%, 68% { opacity: 0.7; }
  100% { transform: translate3d(86px, 0, 0) scaleX(1); opacity: 0; }
`;

const whyIconScan = keyframes`
  0%, 18% { transform: translate3d(-22px, 0, 0); opacity: 0; }
  40%, 64% { opacity: 0.52; }
  100% { transform: translate3d(22px, 0, 0); opacity: 0; }
`;

const whyIconDraw = keyframes`
  0%, 100% { stroke-dashoffset: 112; opacity: 0.58; }
  48%, 76% { stroke-dashoffset: 0; opacity: 0.96; }
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(14px, 1.8vw, 22px);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const WhyCard = styled.article`
  --why-delay: ${(p) => `${(p.$i || 0) * 0.28}s`};
  --why-accent: ${(p) =>
    [
      "rgba(255, 255, 255, 0.78)",
      "rgba(188, 225, 236, 0.82)",
      "rgba(222, 238, 244, 0.8)",
      "rgba(204, 219, 224, 0.78)",
    ][p.$i || 0]};

  position: relative;
  min-height: clamp(250px, 24vw, 320px);
  padding: clamp(22px, 3vw, 34px);
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  background:
    radial-gradient(circle at 14% 12%, rgba(255, 255, 255, 0.13), rgba(255, 255, 255, 0) 32%),
    linear-gradient(150deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025)),
    rgba(5, 6, 8, 0.48);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: clamp(28px, 5vw, 58px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 22px 60px rgba(0, 0, 0, 0.18);
  transition: transform 0.24s ease, border-color 0.24s ease, background 0.24s ease;
  transform: translateZ(0);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      linear-gradient(90deg, rgba(130, 216, 244, 0.32), rgba(255, 255, 255, 0) 38%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
    opacity: 0.38;
    pointer-events: none;
    transition: opacity 0.24s ease;
  }

  &::after {
    content: "";
    position: absolute;
    left: clamp(22px, 3vw, 34px);
    right: clamp(22px, 3vw, 34px);
    bottom: clamp(22px, 3vw, 34px);
    height: 1px;
    background: linear-gradient(90deg, rgba(130, 216, 244, 0.7), rgba(255, 255, 255, 0));
    opacity: 0.56;
    transition: opacity 0.24s ease;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(255, 255, 255, 0.22);
    background:
      radial-gradient(circle at 14% 12%, rgba(130, 216, 244, 0.18), rgba(255, 255, 255, 0) 34%),
      linear-gradient(150deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.028)),
      rgba(5, 6, 8, 0.52);
  }

  &:hover::before,
  &:focus-within::before {
    opacity: 0.56;
  }

  &:hover::after,
  &:focus-within::after {
    opacity: 0.86;
  }

  &:hover [data-why-icon],
  &:focus-within [data-why-icon] {
    transform: translateY(-2px);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.24);
  }

  &:hover [data-why-icon] svg,
  &:focus-within [data-why-icon] svg {
    transform: scale(1.04);
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    [data-why-icon],
    [data-why-icon] svg {
      transition: none !important;
    }

    &:hover {
      transform: none;
    }
  }
`;

const WhyCardSignal = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;

  span {
    position: absolute;
    right: clamp(22px, 3vw, 34px);
    width: clamp(42px, 8vw, 78px);
    height: 1px;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent, var(--why-accent), transparent);
    opacity: 0;
    transform-origin: center;
    animation: ${whyCardSignal} 4.8s ease-in-out infinite;
    animation-delay: calc(var(--why-delay) + 0.25s);
    will-change: transform, opacity;
  }

  span:nth-child(1) {
    top: 26%;
  }

  span:nth-child(2) {
    top: 50%;
    width: clamp(34px, 6vw, 58px);
    animation-delay: calc(var(--why-delay) + 1.05s);
  }

  span:nth-child(3) {
    top: 74%;
    width: clamp(54px, 9vw, 92px);
    animation-delay: calc(var(--why-delay) + 1.85s);
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none !important;
    }
  }
`;

const WhyCardHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
`;

const WhyIconWrap = styled.div`
  position: relative;
  overflow: hidden;
  width: 68px;
  height: 68px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.9);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04)),
    rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.13);
  transition: transform 0.22s ease, border-color 0.22s ease, color 0.22s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 8px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: 13px;
    bottom: 13px;
    width: 18px;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.16), transparent);
    animation: ${whyIconScan} 3.4s ease-in-out infinite;
    animation-delay: var(--why-delay);
    pointer-events: none;
    will-change: transform, opacity;
  }

  svg {
    position: relative;
    z-index: 2;
    transition: transform 0.22s ease;
  }

  svg path,
  svg rect,
  svg circle {
    vector-effect: non-scaling-stroke;
  }

  svg path,
  svg rect {
    stroke-dasharray: 112;
    animation: ${whyIconDraw} 5.2s ease-in-out infinite;
    animation-delay: var(--why-delay);
  }

  @media (prefers-reduced-motion: reduce) {
    &::after,
    svg path,
    svg rect {
      animation: none !important;
    }
  }
`;

const WhyNumber = styled.span`
  font-size: clamp(42px, 6vw, 82px);
  line-height: 0.82;
  font-weight: 800;
  letter-spacing: -0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.12);
`;

const WhyCardBody = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 16px;
  align-content: end;
`;

const WhyTitle = styled.h3`
  margin: 0;
  max-width: 13ch;
  font-size: clamp(32px, 4vw, 54px);
  line-height: 0.95;
  letter-spacing: -0.055em;
`;

const WhyDesc = styled.p`
  margin: 0;
  max-width: 44rem;
  font-size: clamp(16px, 1.55vw, 18px);
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.76);
`;

const ProjectsStack = styled.div`
  display: grid;
  gap: 32px;
`;

const ProjectRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
  gap: clamp(24px, 3vw, 48px);
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: transform 0.22s ease, opacity 0.22s ease;

  &:hover {
    transform: translateY(-4px);
  }

  ${(p) =>
    p.$reverse &&
    `
      direction: rtl;
    `}

  ${(p) =>
    p.$reverse &&
    `
      > * {
        direction: ltr;
      }
    `}

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    direction: ltr;
  }
`;

const ProjectMedia = styled(MarketingMediaSlot)`
  border-radius: 30px;
`;

const ProjectCopy = styled.div`
  display: grid;
  gap: 14px;
  align-content: center;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ProjectLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const ProjectTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
`;

const ProjectTitle = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 52px);
  line-height: 0.98;
`;

const ProjectDesc = styled.p`
  margin: 0;
  max-width: 34rem;
  font-size: 17px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.78);
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const StepNumber = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 28px;
  line-height: 1.06;
`;

const StepDesc = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.78);
`;

const FounderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: clamp(36px, 5vw, 72px);
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const FounderMedia = styled(MarketingMediaSlot)`
  border-radius: 30px;
  max-width: 360px;
`;

const FounderColumn = styled.div`
  display: grid;
  gap: clamp(34px, 4vw, 52px);
`;

const FounderCopy = styled.div`
  max-width: 48rem;
  display: grid;
  gap: 16px;
`;

const FounderKicker = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const FounderName = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 48px);
  line-height: 1;
`;

const FounderText = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.76;
  color: rgba(255, 255, 255, 0.8);
`;

const StrategyBridgePanel = styled(GlassPanel)`
  border-radius: 30px;
  padding: clamp(26px, 4vw, 40px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: end;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const StrategyBridgeCopy = styled.div`
  max-width: 54rem;
  display: grid;
  gap: 12px;
`;

const StrategyBridgeHeading = styled.h3`
  margin: 0;
  font-size: clamp(32px, 4vw, 46px);
  line-height: 1;
`;

const StrategyBridgeText = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.8);
`;

const StrategyBridgeActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const CtaPanel = styled(GlassPanel)`
  border-radius: 32px;
  padding: clamp(28px, 4vw, 44px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const CtaCopy = styled.div`
  max-width: 44rem;
  display: grid;
  gap: 14px;
`;

const CtaHeading = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 1;
`;

const CtaText = styled.p`
  margin: 0;
  font-size: 18px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.8);
`;

const CtaActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const CtaButton = styled.button`
  min-width: 224px;
  min-height: 58px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0 22px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.06)),
    rgba(8, 8, 8, 0.6);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    0 22px 40px rgba(0, 0, 0, 0.32);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 255, 255, 0.24);
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.07)),
      rgba(8, 8, 8, 0.66);
  }
`;

const CtaArrow = styled.span`
  font-size: 18px;
  line-height: 1;
`;
